import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EmailIcon from '@mui/icons-material/Email';
import { Box, Card, Checkbox, FormControlLabel, Grid, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ChangeEvent, MouseEventHandler } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IConsultant } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsIconLocation,
  WsIconText,
  WsIconVisa,
  WsChip,
  WsChipVariants,
  WsButton,
  timeSince,
  WsIconTag,
  WsTagsList,
  ChipsType,
  WsColors,
  WsIconViewJD,
  WsIconTime,
  WsIconPosition,
} from 'wisestep-base';

import { getMaskedPhoneNumber } from '../../features/jobs/helper/JobHelperService';
import ErrorLogger from '../../providers/ErrorLogger';
import PulseIconText from '../PulseIconText';

export interface IActiveConsultantCardProps {
  consultant: IConsultant;
  isCheckBox?: boolean;
  handleCheckBox?: ((event: ChangeEvent<HTMLInputElement>, checked: boolean) => void) | undefined;
  isSelected?: boolean;
  askForResume: () => void;
  onViewDetails?: MouseEventHandler<Element>;
}
const ActiveConsultantCard = ({
  consultant,
  isCheckBox,
  handleCheckBox,
  askForResume,
  isSelected,
  onViewDetails,
}: IActiveConsultantCardProps) => {
  const skills =
    consultant.skills != null ? consultant.skills.map((item) => item.skill ?? 'Not Available') : [];

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Card>
        <Box px={3} py={2}>
          <Grid container spacing={2}>
            <Grid container item xs={12} justifyContent="space-between">
              <FormControlLabel
                control={
                  isCheckBox ? (
                    <Checkbox
                      sx={{ p: 0, mr: 1 }}
                      // onClick={handleCheckBox}
                      onChange={handleCheckBox}
                      size="small"
                      style={{ pointerEvents: 'auto' }}
                      checked={isSelected}
                    />
                  ) : (
                    <></>
                  )
                }
                label={
                  <Grid container item xs={12} alignItems="center">
                    <Box pr={0.5}>
                      <WsTypography
                        variant={WsTypoVariants.TITLE}
                        color={consultant.status ? WsTypoColors.BLACK : WsTypoColors.SECONDARY}
                        cursor="pointer"
                      >
                        {consultant.name ?? ' Anonymous'}
                      </WsTypography>
                    </Box>
                  </Grid>
                }
              />
              <Grid container item xs={6} justifyContent="flex-end">
                <Tooltip title="Status" placement="top">
                  <WsChip
                    value={consultant.status ?? 'Not Available'}
                    variant={
                      consultant.status == 'Active'
                        ? WsChipVariants.SUCCESS
                        : WsChipVariants.TERTIARY
                    }
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid item container xs={12} ml={1}>
              <Grid item xs={3}>
                <WsIconText
                  icon={<WsIconLocation />}
                  text={consultant.location?.country ?? 'Not Available'}
                  opacity={true}
                  popOverText="Consultant Current Location"
                />
              </Grid>
              <Grid item xs={3}>
                <WsIconText
                  icon={<WsIconPosition />}
                  text={consultant.jobTitle ?? 'Confidential'}
                  opacity={true}
                  popOverText="Job Title"
                />
              </Grid>
              <Grid item xs={3}>
                <WsIconText
                  icon={<WsIconVisa />}
                  text={consultant.visaStatus ?? 'Not Available'}
                  opacity={true}
                  popOverText="Visa Status"
                />
              </Grid>
              <Grid item xs={3}>
                <WsIconText
                  icon={<WsIconTime />}
                  text={consultant.availability ?? 'Not Available'}
                  opacity={true}
                  popOverText="Availability"
                />
              </Grid>
            </Grid>
            {!isEmpty(consultant.skills) && (
              <Grid container item xs={12} alignItems="center">
                <Box pt={0.5} pr={0.6}>
                  <WsIconTag />
                </Box>
                <WsTypography
                  variant={WsTypoVariants.SUB_TITLE}
                  color={WsTypoColors.PRIMARY}
                  cursor="pointer"
                >
                  Skills :
                </WsTypography>
                <WsTagsList chipArray={skills} count={3} type={ChipsType.SECONDARY} />
              </Grid>
            )}
            <Grid item container xs={12}>
              <Grid item xs={3}>
                <WsTypography bold>{`Contact Person : ${consultant?.ownerName}`}</WsTypography>
              </Grid>
              <Grid item xs={3}>
                <WsIconText
                  icon={<EmailIcon fontSize="small" />}
                  text={
                    consultant?.ownerId
                      ?.split('@')
                      ?.map(
                        (string, index) =>
                          string.substring(0, 3) + (index === 0 ? 'xxxx' : 'xxxx.com')
                      )
                      .join('@') ?? 'Not Available'
                  }
                  opacity={true}
                  textCursor="pointer"
                  popOverText="Owner Email"
                />
              </Grid>
              {consultant?.ownerPhone && (
                <Grid item xs={3}>
                  <WsIconText
                    icon={<CallRoundedIcon fontSize="small" />}
                    text={getMaskedPhoneNumber(consultant?.ownerPhone) ?? 'Not Available'}
                    opacity={true}
                    textCursor="pointer"
                    popOverText="Owner Phone"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ backgroundColor: WsColors.TheBlues25 }} p={1.5}>
          <Grid container xs={12}>
            <Grid item xs={8}>
              <PulseIconText
                text={
                  consultant.modifiedOn
                    ? `Updated : ${timeSince(consultant.modifiedOn)}`
                    : 'Not Available'
                }
              />
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end">
              <WsButton variant="text" startIcon={<WsIconViewJD />} onClick={() => askForResume()}>
                Ask for Resume
              </WsButton>
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end">
              <WsButton variant="outlined" size="large" onClick={onViewDetails}>
                View Details
              </WsButton>
            </Grid>
          </Grid>
        </Box>
      </Card>
    </ErrorBoundary>
  );
};

export default ActiveConsultantCard;
